/* eslint-disable @next/next/no-img-element */
import {
  discountPillPercent,
  formatPrice,
  formatSubscriptionDateForDisplay,
} from "./formatters";
import { lineTotals } from "./subscription-pricing";
import type { ManageSubscriptionSheetProduct } from "./types";

const QUANTITY_BADGE_CLASS =
  "tw:absolute tw:top-1 tw:left-1 tw:min-w-5 tw:h-5 tw:px-1 tw:rounded tw:bg-[#523D33] tw:text-white tw:text-[10px] tw:font-bold tw:flex tw:items-center tw:justify-center tw:leading-none";

function QuantityBadge({ qty }: { qty: number }) {
  if (qty <= 0) return null;
  return (
    <span className={QUANTITY_BADGE_CLASS} aria-label={`Quantity ${qty}`}>
      {qty}
    </span>
  );
}

export function ManageSubscriptionProductSummary({
  product,
  products,
}: {
  product: ManageSubscriptionSheetProduct;
  products?: ManageSubscriptionSheetProduct[];
}) {
  // NOTE: mirror of src/components/manage-subscription/ManageSubscriptionProductSummary.tsx — keep the multi-product layout in sync.
  const list = products && products.length > 0 ? products : [product];

  if (list.length <= 1) {
    return <SingleProductSummary product={list[0] ?? product} />;
  }

  const statusLower = product.subscription_status?.toLowerCase() ?? "";
  const isFlexible = product.subscription_is_flexible === true;
  const pausedUntilLabel =
    statusLower === "paused" ? product.subscription_paused_until : null;

  const lines = list.map((item) => ({ item, ...lineTotals(item) }));
  const totalNet = lines.some((l) => l.net != null)
    ? lines.reduce((sum, l) => sum + (l.net ?? 0), 0)
    : null;
  const totalOriginal = lines.some((l) => l.original != null)
    ? lines.reduce((sum, l) => sum + (l.original ?? 0), 0)
    : null;

  const statusMeta = isFlexible ? (
    <p
      className="tw:text-xs tw:opacity-80"
      style={{ color: "var(--so-text, #523D33)" }}
    >
      <span className="tw:font-semibold">Flexible</span> — use Get it now to
      order
    </p>
  ) : pausedUntilLabel ? (
    <p
      className="tw:text-xs tw:opacity-80"
      style={{ color: "var(--so-text, #523D33)" }}
    >
      <span className="tw:font-semibold">Paused until:</span>{" "}
      {formatSubscriptionDateForDisplay(pausedUntilLabel)}
    </p>
  ) : statusLower !== "paused" && product.subscription_next_billing_date ? (
    <p className="tw:text-xs tw:opacity-80">
      <span className="tw:text-gray-400">Next delivery:</span>{" "}
      {formatSubscriptionDateForDisplay(product.subscription_next_billing_date)}
    </p>
  ) : null;

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <ul className="tw:flex tw:flex-col">
        {lines.map(({ item, qty, net, original }, idx) => (
          <li
            key={item.id}
            className={
              idx === 0
                ? "tw:flex tw:gap-3 tw:pb-2.5"
                : "tw:flex tw:gap-3 tw:py-2.5 tw:border-t tw:border-gray-100"
            }
          >
            <div className="tw:relative tw:w-14 tw:h-14 tw:bg-[#FDEDEE] tw:rounded-lg tw:shrink-0 tw:overflow-hidden">
              <img
                src={
                  item.image_url ?? "https://instantaccess.me/placeholder.png"
                }
                alt={item.title}
                className="tw:w-full tw:h-full tw:object-cover tw:rounded-lg"
              />
              <QuantityBadge qty={qty} />
            </div>
            <div className="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:justify-center">
              <h3 className="tw:font-medium tw:text-sm tw:line-clamp-2 tw:min-w-0">
                {item.title}
              </h3>
              {item.variant_title ? (
                <p className="tw:text-xs tw:mt-0.5 tw:text-gray-400">
                  {item.variant_title}
                </p>
              ) : null}
            </div>
            {net != null ? (
              <div className="tw:flex tw:flex-col tw:items-end tw:justify-center tw:shrink-0">
                {original != null && original > net ? (
                  <span className="tw:text-xs tw:line-through tw:opacity-50">
                    ${formatPrice(original)}
                  </span>
                ) : null}
                <span
                  className="tw:text-sm tw:font-medium"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  ${formatPrice(net)}
                </span>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {totalNet != null ? (
        <div className="tw:flex tw:items-baseline tw:justify-between tw:pt-2.5 tw:border-t tw:border-gray-100">
          <span
            className="tw:text-sm tw:font-semibold"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            Total
          </span>
          <span className="tw:flex tw:items-baseline tw:gap-1.5">
            {totalOriginal != null && totalOriginal > totalNet ? (
              <span className="tw:text-xs tw:line-through tw:opacity-50">
                ${formatPrice(totalOriginal)}
              </span>
            ) : null}
            <span
              className="tw:text-base tw:font-semibold"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              ${formatPrice(totalNet)}
            </span>
          </span>
        </div>
      ) : null}
      {statusMeta}
    </div>
  );
}

function SingleProductSummary({
  product,
}: {
  product: ManageSubscriptionSheetProduct;
}) {
  const statusLower = product.subscription_status?.toLowerCase() ?? "";
  const isFlexible = product.subscription_is_flexible === true;
  const { qty, net: displayPrice, original: originalPrice } =
    lineTotals(product);
  const discountPercentage = discountPillPercent(
    product.subscription_discounts,
    originalPrice,
    displayPrice,
  );
  const showDiscount = discountPercentage != null;

  const pausedUntilLabel =
    statusLower === "paused" ? product.subscription_paused_until : null;

  return (
    <div className="tw:flex tw:gap-4">
      <div className="tw:relative tw:w-20 tw:h-20 tw:bg-[#FDEDEE] tw:rounded-lg tw:shrink-0 tw:overflow-hidden">
        <img
          src={product.image_url ?? "https://instantaccess.me/placeholder.png"}
          alt={product.title}
          className="tw:w-full tw:h-full tw:object-cover tw:rounded-lg"
        />
        <QuantityBadge qty={qty} />
      </div>
      <div className="tw:flex-1 tw:min-w-0">
        <h3 className="tw:font-medium tw:text-sm tw:line-clamp-3 tw:min-w-0">
          {product.title}
        </h3>
        {product.variant_title ? (
          <p className="tw:text-xs tw:mt-1 tw:text-gray-400">
            {product.variant_title}
          </p>
        ) : null}
        {isFlexible ? (
          <p
            className="tw:text-xs tw:mt-0.5 tw:opacity-80"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            <span className="tw:font-semibold">Flexible</span> — use Get it now
            to order
          </p>
        ) : pausedUntilLabel ? (
          <p
            className="tw:text-xs tw:mt-0.5 tw:opacity-80"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            <span className="tw:font-semibold">Paused until:</span>{" "}
            {formatSubscriptionDateForDisplay(pausedUntilLabel)}
          </p>
        ) : statusLower !== "paused" &&
          product.subscription_next_billing_date ? (
          <p className="tw:text-xs tw:mt-0.5 tw:opacity-80">
            <span className="tw:text-gray-400">Next delivery:</span>{" "}
            {formatSubscriptionDateForDisplay(
              product.subscription_next_billing_date,
            )}
          </p>
        ) : null}
        {displayPrice != null && Number.isFinite(displayPrice) ? (
          <div className="tw:flex tw:items-baseline tw:gap-1.5 tw:mt-1">
            {originalPrice != null &&
              originalPrice > displayPrice &&
              Number.isFinite(originalPrice) && (
                <span className="tw:text-xs tw:line-through tw:opacity-60">
                  ${formatPrice(originalPrice)}
                </span>
              )}
            <span className="tw:text-xs tw:text-gray-400">
              ${formatPrice(displayPrice)}
            </span>
            {showDiscount && discountPercentage != null ? (
              <span className="tw:rounded-full tw:bg-[#FDEDEE] tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:text-[#523D33]">
                {Math.round(discountPercentage)}% off
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
